import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Eye, Edit, Power, Download, FileText, RefreshCw, TrendingUp, BarChart3, Activity, CheckCircle, AlertTriangle, MoreVertical, Trash2, ArrowLeft, LayoutGrid, List as ListIcon, Award } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AssessmentListView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    assessmentType: '',
    dateFrom: '',
    dateTo: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [statusConfirmModal, setStatusConfirmModal] = useState({
    isOpen: false,
    assessmentId: null,
    currentStatus: null
  });
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('assessmentsViewMode') || 'list');
  const [activeTab, setActiveTab] = useState('course');

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('assessmentsViewMode', mode);
  };
  const categoryOptions = Array.from(
    new Set(
      assessments
        .filter((assessment) => (assessment.assessmentType || 'course') === activeTab)
        .map((assessment) => assessment.questionCategory)
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  useEffect(() => {
    fetchAssessments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [assessments, searchTerm, filters, activeTab]);

  useEffect(() => {
    if (showAnalytics && !analyticsData && !analyticsLoading) {
      fetchAnalytics();
    }
  }, [showAnalytics]);

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const { data } = await api.get('/assessments/analytics/global');
      if (data.success) {
        const hasData = data.data.scoreTrendsByBatch?.length > 0 || data.data.departmentAverages?.length > 0;
        if (!hasData) {
          // Provide mock data if db is empty so charts aren't blank
          setAnalyticsData({
            isMockData: true,
            scoreTrendsByBatch: [
              { name: 'Batch 2024-A', score: 85 },
              { name: 'Batch 2024-B', score: 72 },
              { name: 'Batch 2025-A', score: 90 },
              { name: 'Batch 2025-B', score: 68 }
            ],
            departmentAverages: [
              { name: 'Computer Science', average: 88 },
              { name: 'Mechanical', average: 75 },
              { name: 'Electrical', average: 82 },
              { name: 'Civil', average: 70 }
            ],
            completionRate: [
              { name: 'Passed', value: 120 },
              { name: 'Failed', value: 45 }
            ]
          });
        } else {
          setAnalyticsData({ ...data.data, isMockData: false });
        }
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load assessment analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchAssessments = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const [resCourse, resSkill] = await Promise.allSettled([
        api.get('/assessments'),
        api.get('/skill-assessments')
      ]);

      let combinedAssessments = [];
      let fetchFailed = true;

      if (resCourse.status === 'fulfilled' && resCourse.value.data.success) {
        combinedAssessments = [...combinedAssessments, ...(resCourse.value.data.data || [])];
        fetchFailed = false;
      }
      if (resSkill.status === 'fulfilled' && resSkill.value.data.success) {
        // Tag skill assessments explicitly so other views can know their type
        const skillData = (resSkill.value.data.data || []).map(a => ({
          ...a,
          assessmentType: 'skill',
          createdBy: a.createdBy ? {
            ...a.createdBy,
            fullName: a.createdBy.fullName || a.createdBy.name || 'N/A'
          } : null
        }));
        combinedAssessments = [...combinedAssessments, ...skillData];
        fetchFailed = false;
      }

      if (fetchFailed) {
        const reason = (resCourse.status === 'rejected' && resCourse.reason?.message) || 
                       (resSkill.status === 'rejected' && resSkill.reason?.message) ||
                       'Failed to fetch assessments';
        setError(reason);
        toast.error(reason);
      } else {
        setAssessments(combinedAssessments);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
      let errorMessage = 'Failed to fetch assessments';
      if (error.response?.status === 401) {
        errorMessage = 'Please log in to view assessments';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to view assessments';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAssessments();
    toast.success('Assessments refreshed!');
  };

  const applyFilters = () => {
    let filtered = assessments.filter((a) => {
      const type = a.assessmentType || 'course';
      return type === activeTab;
    });

    if (searchTerm) {
      filtered = filtered.filter(
        (assessment) =>
          assessment.assessmentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          assessment.assessmentCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          assessment.questionCategory?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          assessment.createdBy?.fullName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter((assessment) => assessment.status === filters.status);
    }

    if (filters.assessmentType) {
      filtered = filtered.filter((assessment) => assessment.questionCategory === filters.assessmentType);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(
        (assessment) => new Date(assessment.createdAt) >= new Date(filters.dateFrom)
      );
    }
    if (filters.dateTo) {
      filtered = filtered.filter(
        (assessment) => new Date(assessment.createdAt) <= new Date(filters.dateTo)
      );
    }

    setFilteredAssessments(filtered);
    setCurrentPage(1);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const targetAssessment = assessments.find((a) => a._id === id);
      const isSkill = targetAssessment && targetAssessment.assessmentType === 'skill';
      const endpoint = isSkill ? `/skill-assessments/${id}` : `/assessments/${id}`;

      const { data } = await api.put(endpoint, { status: newStatus });
      if (data.success) {
        toast.success(`Assessment ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        fetchAssessments();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.');
    if (!confirmed) return;

    try {
      const targetAssessment = assessments.find(a => a._id === id);
      const isSkill = targetAssessment && targetAssessment.assessmentType === 'skill';
      const endpoint = isSkill ? `/skill-assessments/${id}` : `/assessments/${id}`;

      const { data } = await api.delete(endpoint);
      if (data.success) {
        toast.success('Assessment deleted successfully');
        fetchAssessments();
      }
    } catch (error) {
      console.error('Failed to delete assessment:', error);
      toast.error(error.response?.data?.message || 'Failed to delete assessment');
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text('Assessment List', 14, 22);
    doc.setFontSize(11);
    doc.text(`Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 30);

    const tableData = filteredAssessments.map((assessment) => [
      assessment.assessmentCode || 'N/A',
      assessment.assessmentName || 'N/A',
      assessment.createdBy?.fullName || 'N/A',
      assessment.createdAt ? format(new Date(assessment.createdAt), 'dd/MM/yyyy') : 'N/A',
      assessment.questions?.length || 0,
      assessment.status || 'N/A',
    ]);

    doc.autoTable({
      head: [['Code', 'Assessment Name', 'Created By', 'Date', 'Questions', 'Status']],
      body: tableData,
      startY: 35,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [99, 102, 241] },
    });

    doc.save(`assessments-list-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exported successfully');
  };

  const exportToExcel = () => {
    const excelData = filteredAssessments.map((assessment) => ({
      'Assessment Code': assessment.assessmentCode || 'N/A',
      'Assessment Name': assessment.assessmentName || 'N/A',
      'Description': assessment.description || 'N/A',
      'Category': assessment.questionCategory || 'N/A',
      'Created By': assessment.createdBy?.fullName || 'N/A',
      'Created Date': assessment.createdAt ? format(new Date(assessment.createdAt), 'dd/MM/yyyy') : 'N/A',
      'Status': assessment.status || 'N/A',
      'Total Attempts': assessment.totalAttempts || 0,
      'Total Questions': assessment.questions?.length || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assessments');
    XLSX.writeFile(wb, `assessments-list-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAssessments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAssessments.length / itemsPerPage);

  const filteredByType = assessments.filter(a => (a.assessmentType || 'course') === activeTab);
  const activeCount = filteredByType.filter(a => a.status === 'active').length;
  const draftCount = filteredByType.filter(a => a.status === 'draft').length;
  const inactiveCount = filteredByType.filter(a => a.status === 'inactive').length;
  const hasActiveFilters = searchTerm || Object.values(filters).some(Boolean);

  const StatCard = ({ icon: Icon, label, value, gradient, loading }) => (
    <div className="group relative bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/35 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
      <div className="relative flex items-center justify-between">
        <div className="flex-1">
          <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</p>
          {loading ? (
            <div className="h-6 bg-gray-200 rounded animate-pulse w-12"></div>
          ) : (
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          )}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="text-white" size={18} />
        </div>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-800' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800' },
      draft: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="glass dark:bg-[#002147]/80 rounded-xl shadow-md border border-white/30 dark:border-[#1a3884]/30 p-4 mb-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] dark:hover:border-[#1a3884] dark:hover:text-white rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
              title="Back to Dashboard"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#00152E] dark:text-white mb-1 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1a3884]" />
                Assessment List View
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <span className="font-medium">Manage and analyze all assessments</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto justify-start sm:justify-end flex-wrap">
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 text-sm px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 rounded-xl transition-colors font-medium shadow-sm"
              title="Export as Excel"
            >
              <Download size={16} className="text-gray-500 dark:text-gray-400" />
              <span className="hidden sm:inline">Export</span>
            </button>

            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="btn-secondary flex items-center justify-center gap-2 text-sm px-3 py-2 whitespace-nowrap bg-white dark:bg-[#00152e] text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 rounded-xl"
            >
              {showAnalytics ? <TrendingUp size={14} /> : <BarChart3 size={14} />}
              <span className="hidden sm:inline">{showAnalytics ? 'Hide Analytics' : 'Show Analytics'}</span>
              <span className="sm:hidden">Analytics</span>
            </button>

            <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#00152e] p-1 rounded-xl border border-gray-200 dark:border-[#1a3884]/50 mx-2">
              <button
                onClick={() => toggleViewMode('grid')}
                className={`p-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-all duration-300 ${viewMode === 'grid' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              >
                <LayoutGrid size={16} /> <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => toggleViewMode('list')}
                className={`p-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-all duration-300 ${viewMode === 'list' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              >
                <ListIcon size={16} /> <span className="hidden sm:inline">List</span>
              </button>
            </div>

            <button
              onClick={() => navigate('/assessments/create', { state: { type: activeTab } })}
              className="flex items-center gap-2 text-sm px-4 py-2 bg-[#1a3884] hover:bg-[#00152E] dark:bg-[#1a3884] dark:hover:bg-[#00152E] text-white font-semibold rounded-xl transition-all shadow-md shadow-[#1a3884]/15 hover:shadow-lg hover:shadow-[#1a3884]/20 transform hover:-translate-y-0.5 transition-colors border border-[#00152E]/20"
            >
              <Plus size={14} />
              <span className="hidden sm:inline whitespace-nowrap">Create Assessment</span>
              <span className="sm:hidden">Create</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 p-1 bg-gray-100 dark:bg-[#00152e] rounded-xl border border-gray-200 dark:border-[#1a3884]/50 mb-6">
        <button
          onClick={() => setActiveTab('course')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all duration-300 ${
            activeTab === 'course'
              ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <FileText size={16} />
          Course Assessment
        </button>
        <button
          onClick={() => setActiveTab('skill')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all duration-300 ${
            activeTab === 'skill'
              ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <Award size={16} />
          Skill Assessment
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={FileText} label="Total Assessments" value={filteredByType.length} gradient="from-[#00152E] to-[#1a3884]" loading={loading} />
        <StatCard icon={CheckCircle} label="Active" value={activeCount} gradient="from-[#00152E] to-[#1a3884]" loading={loading} />
        <StatCard icon={AlertTriangle} label="Draft" value={draftCount} gradient="from-[#00152E] to-[#1a3884]" loading={loading} />
        <StatCard icon={Activity} label="Inactive" value={inactiveCount} gradient="from-[#00152E] to-[#1a3884]" loading={loading} />
      </div>

      {/* Analytics Section */}
      {showAnalytics && user?.role !== 'office_admin' && (
        <div className="card-hover mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-bold text-gray-900">Global Assessment Analytics</h2>
              {analyticsData?.isMockData && (
                <span className="px-2 py-1 text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-200 rounded-full">
                  Sample Data (No Responses Yet)
                </span>
              )}
            </div>
          </div>
          {analyticsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3884]"></div>
            </div>
          ) : analyticsData ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Batch Trends */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a3884]/5 flex items-center justify-center border border-[#1a3884]/10">
                    <TrendingUp className="w-4 h-4 text-[#1a3884]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Score Trends by Batch</h3>
                </div>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analyticsData.scoreTrendsByBatch}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 11 }} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 11 }} dx={-10} domain={[0, 100]} />
                      <Tooltip cursor={{ fill: '#F3F4F6' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                      <Line type="monotone" dataKey="score" stroke="#1a3884" strokeWidth={3} dot={{ r: 4, fill: '#1a3884', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Department Averages */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a3884]/5 flex items-center justify-center border border-[#1a3884]/10">
                    <BarChart3 className="w-4 h-4 text-[#1a3884]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Department-wise Average</h3>
                </div>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.departmentAverages}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 11 }} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 11 }} dx={-10} domain={[0, 100]} />
                      <Tooltip cursor={{ fill: '#F3F4F6' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                      <Bar dataKey="average" fill="#1a3884" radius={[4, 4, 0, 0]} maxBarSize={35} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Completion vs Non-completion */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a3884]/5 flex items-center justify-center border border-[#1a3884]/10">
                    <CheckCircle className="w-4 h-4 text-[#1a3884]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Pass vs Fail Rate</h3>
                </div>
                <div className="h-[220px] relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData.completionRate}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {analyticsData.completionRate.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#1a3884' : '#9CA3AF'} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

            </div>
          ) : (
            <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500 text-sm">
              No analytics data available yet.
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/35 shadow-sm p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-100 mb-4 flex items-center gap-2">Filters & Search</h2>
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search assessments by name, code, or creator..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-[#1a3884]/35 bg-white dark:bg-[#00152E] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884] transition-all"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <input
                type="date"
                title="From Date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 focus:bg-white outline-none text-gray-700 w-[135px] transition-all"
              />
              <span className="text-gray-400 text-sm font-medium">to</span>
              <input
                type="date"
                title="To Date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 focus:bg-white outline-none text-gray-700 w-[135px] transition-all"
              />
            </div>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 hover:bg-white focus:bg-white outline-none text-gray-700 min-w-[130px] transition-all"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>

            <select
              value={filters.assessmentType}
              onChange={(e) => setFilters({ ...filters, assessmentType: e.target.value })}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 hover:bg-white focus:bg-white outline-none text-gray-700 min-w-[130px] transition-all"
            >
              <option value="">{activeTab === 'skill' ? 'All Skills' : 'All Categories'}</option>
              {categoryOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-2 border-l border-gray-200 dark:border-[#1a3884]/40 pl-3 ml-1">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-gray-500 hover:text-[#1a3884] hover:bg-indigo-50 rounded-xl transition-colors border border-gray-200 bg-white shadow-sm"
                title="Refresh"
              >
                <RefreshCw className={refreshing ? 'animate-spin text-[#1a3884]' : ''} size={16} />
              </button>
            </div>

            {hasActiveFilters && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilters({ status: '', assessmentType: '', dateFrom: '', dateTo: '' });
                }}
                className="px-3 py-2 text-sm text-gray-500 hover:text-red-600 font-medium transition-colors ml-1 hover:bg-red-50 rounded-xl"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content View (Grid or List) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 px-2">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">Assessments Directory</h2>
          <span className="text-sm text-slate-400 dark:text-slate-300 font-medium">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredAssessments.length)} of {filteredAssessments.length} assessments
          </span>
        </div>

        {filteredAssessments.length > 0 ? (
          <>
            {viewMode === 'list' ? (
              <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/35 shadow-sm overflow-hidden">
                <div className="overflow-x-auto pb-12">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-[#001f40] border-b border-gray-200 dark:border-[#1a3884]/30">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Assessment Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Uploaded By</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Created Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Questions</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-[#1a3884]/20 bg-white dark:bg-[#002147]">
                      {currentItems.map((assessment) => (
                        <tr
                          key={assessment._id}
                          className={`hover:bg-gray-50 dark:hover:bg-[#001f40] transition-all duration-200 border-b border-gray-100 dark:border-[#1a3884]/20 ${openDropdown === assessment._id ? 'relative z-50' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 font-mono">
                              {assessment.assessmentCode || 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-bold text-gray-900 dark:text-gray-100">{assessment.assessmentName}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 bg-gray-100 dark:bg-[#00152e] inline-block px-1.5 py-0.5 rounded mt-0.5">{assessment.questionCategory}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{assessment.createdBy?.fullName || 'N/A'}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">{assessment.createdBy?.role || ''}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {assessment.createdAt ? format(new Date(assessment.createdAt), 'dd/MM/yyyy') : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {assessment.questions?.length || 0} questions
                          </td>

                          <td className="px-4 py-3">{getStatusBadge(assessment.status)}</td>
                          <td className="px-4 py-3 text-right relative">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => navigate(`/assessments/view/${assessment._id}`)}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-[#1a3884] dark:text-[#4f7dff] bg-[#00152E]/5 dark:bg-[#1a3884]/20 hover:bg-[#00152E]/10 dark:hover:bg-[#1a3884]/40 rounded-lg transition-all"
                                title="View Details"
                              >
                                View
                              </button>

                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(openDropdown === assessment._id ? null : assessment._id);
                                  }}
                                  className="p-1.5 text-slate-400 dark:text-slate-300 hover:text-[#1a3884] dark:hover:text-[#4f7dff] rounded-lg hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884]/45 transition-all"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                                {openDropdown === assessment._id && (
                                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/40 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] z-[100] py-1.5 overflow-visible text-left">
                                    <button
                                      onClick={() => {
                                        navigate(`/assessments/edit/${assessment._id}`);
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-[12px] text-gray-700 dark:text-slate-200 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884]/45 hover:text-[#1a3884] dark:hover:text-[#4f7dff] flex items-center transition-colors"
                                    >
                                      <Edit className="w-3.5 h-3.5 mr-2.5" />
                                      Edit Assessment
                                    </button>
                                    <button
                                      onClick={() => {
                                        setStatusConfirmModal({
                                          isOpen: true,
                                          assessmentId: assessment._id,
                                          currentStatus: assessment.status
                                        });
                                        setOpenDropdown(null);
                                      }}
                                      className={`w-full px-4 py-2 text-left text-[12px] flex items-center transition-colors ${assessment.status === 'active'
                                          ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20'
                                          : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20'
                                        }`}
                                    >
                                      {assessment.status === 'active' ? (
                                        <>
                                          <Power className="w-3.5 h-3.5 mr-2.5" />
                                          Inactive
                                        </>
                                      ) : (
                                        <>
                                          <Power className="w-3.5 h-3.5 mr-2.5" />
                                          Active
                                        </>
                                      )}
                                    </button>
                                    <div className="h-px bg-gray-100 dark:bg-[#1a3884]/20 my-1"></div>
                                    <button
                                      onClick={() => {
                                        handleDelete(assessment._id);
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-[12px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/25 flex items-center transition-colors font-medium"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 mr-2.5" />
                                      Delete Assessment
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {currentItems.map(assessment => (
                  <div key={assessment._id} className="group bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-[#1a3884]/30 p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden flex flex-col">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00152E] to-[#1a3884] dark:from-[#4f7dff] dark:to-[#1a3884] opacity-0 group-hover:opacity-100 transition-opacity"></div>

                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-[#00152e] border border-indigo-100 dark:border-[#1a3884]/50 flex items-center justify-center text-lg font-bold text-[#1a3884] dark:text-[#4f7dff] shadow-sm shrink-0">
                        <FileText className="w-6 h-6" />
                      </div>
                      {getStatusBadge(assessment.status)}
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight mb-2 line-clamp-2" title={assessment.assessmentName}>{assessment.assessmentName}</h3>
                    <div className="mb-3 text-xs text-slate-500 dark:text-slate-400 font-mono">
                      {assessment.assessmentCode || 'Pending Code'}
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-xs bg-gray-100 dark:bg-[#001f40]/50 text-gray-600 dark:text-gray-300 px-2 py-1 rounded border border-gray-200 dark:border-[#1a3884]/20">
                        {assessment.questionCategory}
                      </span>
                    </div>

                    <div className="space-y-2 mt-auto pt-4 border-t border-gray-100 dark:border-[#1a3884]/30">
                      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 font-medium">
                        <span>Questions:</span>
                        <span className="font-bold text-gray-800 dark:text-gray-100">{assessment.questions?.length || 0}</span>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 font-medium">
                        <span>Creator:</span>
                        <span>{assessment.createdBy?.fullName || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-5">
                      <button
                        onClick={() => navigate(`/assessments/view/${assessment._id}`)}
                        className="w-full py-2 bg-gray-50 dark:bg-[#1a3884]/20 hover:bg-gray-100 dark:hover:bg-[#1a3884]/40 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 transition-colors border border-gray-200 dark:border-[#1a3884]/30"
                      >
                        <Eye size={14} /> View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown(openDropdown === assessment._id ? null : assessment._id);
                        }}
                        className="w-full py-2 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 transition-colors border border-blue-100 dark:border-blue-900/30 relative"
                      >
                        <MoreVertical size={14} /> Actions
                        {openDropdown === assessment._id && (
                          <div className="absolute bottom-full right-0 mb-2 w-44 bg-white dark:bg-[#002147] border border-gray-200 dark:border-[#1a3884]/40 rounded-lg shadow-xl z-[100] py-1 overflow-visible text-left">
                            <button
                              onClick={() => { navigate(`/assessments/edit/${assessment._id}`); setOpenDropdown(null); }}
                              className="w-full px-4 py-2.5 text-left text-xs text-gray-700 dark:text-slate-200 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884]/45 flex items-center"
                            >
                              <Edit className="w-3.5 h-3.5 mr-2.5" /> Edit
                            </button>
                            <button
                              onClick={() => {
                                setStatusConfirmModal({
                                  isOpen: true,
                                  assessmentId: assessment._id,
                                  currentStatus: assessment.status
                                });
                                setOpenDropdown(null);
                              }}
                              className={`w-full px-4 py-2.5 text-left text-xs flex items-center transition-colors ${assessment.status === 'active'
                                  ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20'
                                  : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20'
                                }`}
                            >
                              {assessment.status === 'active' ? (
                                <><Power className="w-3.5 h-3.5 mr-2.5" /> Inactive</>
                              ) : (
                                <><Power className="w-3.5 h-3.5 mr-2.5" /> Active</>
                              )}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(assessment._id);
                                setOpenDropdown(null);
                              }}
                              className="w-full px-4 py-2.5 text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 flex items-center"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-2.5" /> Delete
                            </button>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-[#1a3884]/30 pt-3">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-gray-350 dark:border-[#1a3884]/30 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-[#002147] hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 border border-gray-350 dark:border-[#1a3884]/30 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-[#002147] hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-gray-100 dark:border-[#1a3884]/30">
            <FileText className="w-14 h-14 text-gray-200 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
              {error ? 'Error Loading Assessments' : 'No Assessments Found'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {error ? (
                <span className="text-red-600">{error}</span>
              ) : assessments.length === 0 ? (
                "No assessments have been created yet. Click 'Create Assessment' to get started."
              ) : (
                "No assessments match your current filters. Try adjusting your search criteria."
              )}
            </p>
            {!error && assessments.length === 0 && (
              <button
                onClick={() => navigate('/assessments/create')}
                className="flex items-center gap-2 text-sm px-5 py-2 bg-[#1a3884] hover:bg-[#00152E] dark:bg-[#1a3884] dark:hover:bg-[#00152E] text-white font-semibold rounded-xl transition-all shadow-md shadow-[#1a3884]/15 hover:shadow-lg hover:shadow-[#1a3884]/20 transform hover:-translate-y-0.5 mx-auto border border-[#00152E]/20"
              >
                <Plus size={14} />
                <span>Create Your First Assessment</span>
              </button>
            )}
            {error && (
              <button
                onClick={fetchAssessments}
                className="flex items-center gap-2 text-sm px-4 py-2 mx-auto bg-gray-100 text-gray-700 rounded-lg"
              >
                <RefreshCw size={14} />
                <span>Try Again</span>
              </button>
            )}
          </div>
        )}
        {/* Status Confirmation Modal */}
        {statusConfirmModal.isOpen && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-[#002147] rounded-3xl p-6 shadow-2xl border border-slate-200/60 dark:border-[#1a3884]/45 max-w-sm w-full text-center space-y-4 animate-in fade-in zoom-in duration-200">
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {statusConfirmModal.currentStatus === 'active' ? 'Deactivate Assessment' : 'Activate Assessment'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">
                  Are you sure you want to change the status of this assessment to{' '}
                  <span className="font-semibold text-gray-800 dark:text-slate-200">
                    {statusConfirmModal.currentStatus === 'active' ? 'Inactive' : 'Active'}
                  </span>
                  ?
                </p>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setStatusConfirmModal({ isOpen: false, assessmentId: null, currentStatus: null })}
                  className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-[#1a3884]/45 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-50 dark:hover:bg-[#1a3884]/20 text-xs font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleToggleStatus(statusConfirmModal.assessmentId, statusConfirmModal.currentStatus);
                    setStatusConfirmModal({ isOpen: false, assessmentId: null, currentStatus: null });
                  }}
                  className={`flex-1 px-4 py-2.5 text-white rounded-xl text-xs font-bold transition-all ${statusConfirmModal.currentStatus === 'active'
                      ? 'bg-amber-600 hover:bg-amber-700 shadow-md shadow-amber-600/10'
                      : 'bg-[#1a3884] hover:bg-[#00152E] shadow-md shadow-[#1a3884]/10'
                    }`}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentListView;
